// Вопрос агента с вариантами (AskUserQuestion) — так, как его рисует claude
// code в терминале:
//
//   ☐ Доставка  ☐ Формат  ✔ Отправить            ← вкладки, если вопросов > 1
//
//   Как удобнее забрать файл с сервера?
//
//   ❯ 1. TG-бот, пережать до 50 МБ
//        Отправлю одним файлом в бота…
//     2. Ссылкой
//     3. Свой вариант…
//
//   Enter — выбрать · ↑/↓ — навигация · Esc — отменить
//
// Ответ уходит агенту через updatedInput.answers: {текст вопроса: ответ}.
// Без него CLI отвечает агенту «The user did not answer the questions» —
// именно так вопросы и «не работали», пока тут была общая карточка прав.

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PermissionRequest } from '../protocol'
import { usePane } from '../store/logStore'
import type { PermStatus } from '../store/logStore'
import type { PermissionOptions } from '../transport/ws'
import './question.css'

interface Option { label: string; description?: string }
interface Question {
  question: string
  header?: string
  multiSelect?: boolean
  options: Option[]
}

interface Props {
  sessionId: string
  perm: PermissionRequest
  onRespond(id: string, behavior: 'allow' | 'deny', opts?: PermissionOptions): void
  /** Телефон: крупнее цели касания, без подсказок про клавиши. */
  touch?: boolean
}

interface Answer {
  /** индексы выбранных вариантов; индекс options.length — «свой вариант» */
  picked: number[]
  other: string
}

export function parseQuestions(input: Record<string, unknown> | undefined): Question[] {
  const raw = input?.questions
  if (!Array.isArray(raw)) return []
  return raw
    .filter((q): q is Record<string, unknown> => !!q && typeof q === 'object')
    .map((q) => ({
      question: String(q.question ?? ''),
      header: typeof q.header === 'string' ? q.header : undefined,
      multiSelect: q.multiSelect === true,
      options: Array.isArray(q.options)
        ? q.options
          .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
          .map((o) => ({
            label: String(o.label ?? ''),
            description: typeof o.description === 'string' ? o.description : undefined,
          }))
        : [],
    }))
}

function answerText(q: Question, a: Answer | undefined): string {
  if (!a) return ''
  const parts = a.picked
    .slice()
    .sort((x, y) => x - y)
    .map((i) => (i === q.options.length ? a.other.trim() : q.options[i]?.label ?? ''))
    .filter(Boolean)
  return parts.join(', ')
}

const EMPTY: Answer = { picked: [], other: '' }

/** Общее у React- и DOM-события клавиатуры — одна обработка на оба пути. */
interface KeyLike {
  key: string
  shiftKey: boolean
  target: EventTarget | null
  preventDefault(): void
  stopPropagation(): void
}

const KEYS = new Set([
  'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ',
  '1', '2', '3', '4', '5', '6', '7', '8', '9',
])

function editable(el: Element): el is HTMLInputElement | HTMLTextAreaElement {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
}

/**
 * Ловить ли клавишу, нажатую вне карточки. В консоли, пока висит вопрос,
 * ввода нет вовсе — здесь поле ввода окна остаётся, поэтому клавиши берём,
 * если фокус не в поле (body, кнопка шапки) и окно активное, или в ПУСТОМ
 * поле этого окна.
 * Набранный текст не трогаем: человек, возможно, пишет агенту.
 */
function ownsKey(card: HTMLElement, target: EventTarget | null): boolean {
  if (target instanceof Element && card.contains(target)) return false
  const pane = card.closest('.ab-pane')
  const active = !pane || pane.classList.contains('ab-pane--active')
  if (!(target instanceof Element) || target === document.body) return active
  const inPane = !!pane && pane.contains(target)
  if (editable(target) || (target as HTMLElement).isContentEditable) {
    return inPane && editable(target) && target.value === ''
  }
  // кнопка или ссылка: своя панель — да; чужая панель — нет; шапка — если окно активное
  if (inPane) return true
  return active && !target.closest('.ab-pane')
}

function QuestionCard({ sessionId, perm, onRespond, touch = false }: Props) {
  const open = usePane(sessionId, (s) => s.pending.some((p) => p.id === perm.id))
  const status = usePane(sessionId, (s) => s.permStatus[perm.id] as PermStatus | undefined)

  const questions = useMemo(() => parseQuestions(perm.input), [perm.input])
  const multi = questions.length > 1
  // вкладка questions.length — «Отправить» (обзор ответов), как в консоли
  const [tab, setTab] = useState(0)
  const [cursor, setCursor] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>(() => questions.map(() => ({ ...EMPTY })))
  const [sent, setSent] = useState<Record<string, string> | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const otherRef = useRef<HTMLInputElement>(null)

  const onSubmitTab = multi && tab === questions.length
  const q = onSubmitTab ? undefined : questions[tab]
  const otherIdx = q ? q.options.length : -1
  const rows = q ? q.options.length + 1 : 2

  // Карточка забирает фокус, когда появляется, — чтобы стрелки и Enter
  // работали сразу, как в терминале.
  useEffect(() => {
    if (open && !touch) rootRef.current?.focus({ preventScroll: false })
  }, [open, touch])

  useEffect(() => {
    if (q && cursor === otherIdx && open) otherRef.current?.focus()
    else if (open && !touch && document.activeElement === otherRef.current) rootRef.current?.focus()
  }, [cursor, otherIdx, q, open, touch])

  const finish = useCallback((list: Answer[]) => {
    const result: Record<string, string> = {}
    questions.forEach((qq, i) => { result[qq.question] = answerText(qq, list[i]) })
    setSent(result)
    onRespond(perm.id, 'allow', { updatedInput: { ...perm.input, answers: result } })
  }, [questions, onRespond, perm.id, perm.input])

  const cancel = useCallback(() => {
    onRespond(perm.id, 'deny', { message: 'оператор отменил вопрос' })
  }, [onRespond, perm.id])

  const goTab = useCallback((next: number) => {
    const last = multi ? questions.length : questions.length - 1
    const t = Math.max(0, Math.min(last, next))
    setTab(t)
    setCursor(0)
  }, [multi, questions.length])

  /** Вопрос отвечен — дальше следующая вкладка или отправка. */
  const advance = useCallback((list: Answer[]) => {
    if (!multi) { finish(list); return }
    const nextEmpty = list.findIndex((a, i) => i > tab && a.picked.length === 0)
    goTab(nextEmpty === -1 ? questions.length : nextEmpty)
  }, [multi, finish, tab, goTab, questions.length])

  const choose = useCallback((idx: number) => {
    if (!q) return
    const cur = answers[tab] ?? EMPTY
    if (idx === otherIdx && !cur.other.trim()) {
      setCursor(idx)
      otherRef.current?.focus()
      return
    }
    let picked: number[]
    if (q.multiSelect) {
      picked = cur.picked.includes(idx)
        ? cur.picked.filter((i) => i !== idx)
        : [...cur.picked, idx]
    } else {
      picked = [idx]
    }
    const list = answers.map((a, i) => (i === tab ? { ...a, picked } : a))
    setAnswers(list)
    setCursor(idx)
    if (!q.multiSelect) advance(list)
  }, [q, answers, tab, otherIdx, advance])

  const setOther = useCallback((text: string) => {
    setAnswers((list) => list.map((a, i) => {
      if (i !== tab) return a
      const has = a.picked.includes(otherIdx)
      const picked = text.trim()
        ? (has ? a.picked : q?.multiSelect ? [...a.picked, otherIdx] : [otherIdx])
        : a.picked.filter((x) => x !== otherIdx)
      return { ...a, other: text, picked }
    }))
  }, [tab, otherIdx, q?.multiSelect])

  const onKey = useCallback((e: KeyLike) => {
    if (!open) return
    const inOther = e.target === otherRef.current
    const key = e.key
    const handled = () => { e.preventDefault(); e.stopPropagation() }

    if (key === 'Escape') { handled(); cancel(); return }
    if (key === 'ArrowUp') { handled(); setCursor((c) => (c - 1 + rows) % rows); return }
    if (key === 'ArrowDown' || (key === 'Tab' && !e.shiftKey && !inOther)) {
      handled(); setCursor((c) => (c + 1) % rows); return
    }
    if (inOther) {
      if (key === 'Enter') {
        handled()
        if (!q) return
        const cur = answers[tab] ?? EMPTY
        if (q.multiSelect) { if (cur.picked.length) advance(answers) }
        else if (cur.other.trim()) advance(answers.map((a, i) => (i === tab ? { ...a, picked: [otherIdx] } : a)))
      }
      return
    }
    if (key === 'ArrowLeft') { handled(); goTab(tab - 1); return }
    if (key === 'ArrowRight') { handled(); goTab(tab + 1); return }
    if (onSubmitTab) {
      if (key === 'Enter') { handled(); cursor === 0 ? finish(answers) : cancel() }
      else if (key === '1') { handled(); finish(answers) }
      else if (key === '2') { handled(); cancel() }
      return
    }
    if (!q) return
    if (/^[1-9]$/.test(key)) {
      const idx = Number(key) - 1
      if (idx < rows) { handled(); choose(idx) }
      return
    }
    if (key === ' ' && q.multiSelect) { handled(); choose(cursor); return }
    if (key === 'Enter') {
      handled()
      if (q.multiSelect) {
        // Enter в мультивыборе — «готово», если что-то отмечено; иначе отметить
        if ((answers[tab]?.picked.length ?? 0) > 0) advance(answers)
        else choose(cursor)
      } else {
        choose(cursor)
      }
    }
  }, [open, rows, cancel, q, answers, tab, advance, otherIdx, goTab, onSubmitTab, cursor, finish, choose])

  const onKeyRef = useRef(onKey)
  onKeyRef.current = onKey

  useEffect(() => {
    if (!open || touch) return
    const h = (e: KeyboardEvent) => {
      const card = rootRef.current
      if (!card || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return
      if (!KEYS.has(e.key) || !ownsKey(card, e.target)) return
      onKeyRef.current(e)
    }
    document.addEventListener('keydown', h, true)
    return () => document.removeEventListener('keydown', h, true)
  }, [open, touch])

  // ---------- уже отвеченный / закрытый вопрос: сводка, как в консоли ----------

  if (!open) {
    const given = sent ?? (perm.input?.answers as Record<string, string> | undefined)
    const denied = status === 'denied' || status === 'expired'
    return (
      <div className="t-q t-q--done">
        <div className="t-q__line">
          <span className="t-gut t-q__gut">⏺</span>
          <span>
            {denied
              ? status === 'expired' ? 'Вопрос истёк без ответа' : 'Вопрос отменён'
              : given ? 'Ответы на вопросы:' : 'Вопрос закрыт'}
          </span>
        </div>
        {!denied && given
          ? questions.map((qq, i) => (
            <div className="t-q__line t-q__sum" key={i}>
              <span className="t-gut">{i === 0 ? '⎿' : ''}</span>
              <span>
                · {qq.question} <span className="t-q__arrow">→</span>{' '}
                <b>{given[qq.question] || '—'}</b>
              </span>
            </div>
          ))
          : null}
      </div>
    )
  }

  // ---------- живой вопрос ----------

  const cur = answers[tab] ?? EMPTY

  return (
    <div
      ref={rootRef}
      className={`t-q t-q--open${touch ? ' t-q--touch' : ''}`}
      tabIndex={-1}
      onKeyDown={onKey}
    >
      {multi ? (
        <div className="t-q__tabs">
          <span className="t-q__tabarrow">←</span>
          {questions.map((qq, i) => {
            const done = (answers[i]?.picked.length ?? 0) > 0
            return (
              <button
                key={i}
                type="button"
                className={`t-q__tab${i === tab ? ' t-q__tab--on' : ''}`}
                onClick={() => goTab(i)}
              >
                {done ? '☒' : '☐'} {qq.header || `Вопрос ${i + 1}`}
              </button>
            )
          })}
          <button
            type="button"
            className={`t-q__tab${onSubmitTab ? ' t-q__tab--on' : ''}`}
            onClick={() => goTab(questions.length)}
          >
            ✔ Отправить
          </button>
          <span className="t-q__tabarrow">→</span>
        </div>
      ) : q?.header ? (
        <div className="t-q__tabs">
          <span className="t-q__tab t-q__tab--on t-q__tab--static">☐ {q.header}</span>
        </div>
      ) : null}

      {q ? (
        <>
          <div className="t-q__title">{q.question}</div>
          <div className="t-q__opts" role="listbox" aria-multiselectable={q.multiSelect || undefined}>
            {q.options.map((o, i) => {
              const on = cur.picked.includes(i)
              const here = cursor === i
              return (
                <button
                  key={i}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`t-q__opt${here ? ' t-q__opt--cur' : ''}${on ? ' t-q__opt--on' : ''}`}
                  onMouseEnter={() => !touch && setCursor(i)}
                  onClick={() => choose(i)}
                >
                  <span className="t-q__ptr">{here ? '❯' : ''}</span>
                  <span className="t-q__num">{i + 1}.</span>
                  <span className="t-q__body">
                    <span className="t-q__label">
                      {q.multiSelect ? (on ? '[✔] ' : '[ ] ') : null}
                      {o.label}
                      {!q.multiSelect && on ? ' ✔' : null}
                    </span>
                    {o.description ? <span className="t-q__desc">{o.description}</span> : null}
                  </span>
                </button>
              )
            })}
            <div
              className={`t-q__opt t-q__opt--other${cursor === otherIdx ? ' t-q__opt--cur' : ''}`}
              onMouseEnter={() => !touch && setCursor(otherIdx)}
              onClick={() => { setCursor(otherIdx); otherRef.current?.focus() }}
            >
              <span className="t-q__ptr">{cursor === otherIdx ? '❯' : ''}</span>
              <span className="t-q__num">{otherIdx + 1}.</span>
              <span className="t-q__body">
                <input
                  ref={otherRef}
                  className="t-q__other"
                  value={cur.other}
                  placeholder="Свой вариант…"
                  onFocus={() => setCursor(otherIdx)}
                  onChange={(e) => setOther(e.target.value)}
                />
              </span>
            </div>
          </div>
          {q.multiSelect || touch ? (
            <div className="t-q__actions">
              {q.multiSelect || cur.picked.includes(otherIdx) ? (
                <button
                  type="button"
                  className="t-q__go"
                  disabled={cur.picked.length === 0}
                  onClick={() => advance(answers)}
                >
                  {multi ? 'Дальше →' : 'Готово'}
                </button>
              ) : null}
              {touch ? (
                <button type="button" className="t-q__cancel" onClick={cancel}>отменить</button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="t-q__title">Проверьте ответы</div>
          <div className="t-q__review">
            {questions.map((qq, i) => {
              const text = answerText(qq, answers[i])
              return (
                <div className="t-q__sum" key={i}>
                  · {qq.question} <span className="t-q__arrow">→</span>{' '}
                  {text ? <b>{text}</b> : <span className="t-q__missing">нет ответа</span>}
                </div>
              )
            })}
          </div>
          <div className="t-q__opts">
            {['Отправить ответы', 'Отменить'].map((label, i) => (
              <button
                key={i}
                type="button"
                className={`t-q__opt${cursor === i ? ' t-q__opt--cur' : ''}`}
                onMouseEnter={() => !touch && setCursor(i)}
                onClick={() => (i === 0 ? finish(answers) : cancel())}
              >
                <span className="t-q__ptr">{cursor === i ? '❯' : ''}</span>
                <span className="t-q__num">{i + 1}.</span>
                <span className="t-q__body"><span className="t-q__label">{label}</span></span>
              </button>
            ))}
          </div>
        </>
      )}

      {touch ? null : (
        <div className="t-q__hint">
          {q?.multiSelect
            ? 'Пробел — отметить · Enter — готово · ↑/↓ — навигация'
            : 'Enter — выбрать · ↑/↓ — навигация'}
          {multi ? ' · ←/→ — вопросы' : ''} · Esc — отменить
        </div>
      )}
    </div>
  )
}

export default memo(QuestionCard)
